import type {
  ApiChannelGuide,
  ApiContentItem,
  ApiFile,
  ApiManifest,
} from '~/api/manifest.generated';
import type { PreviewProps } from '~/components/preview';
import { assert } from './assert';
import type {
  Preview__ContentItem as ContentItem,
  Preview__Playlist as Playlist,
  Preview__Device,
  Preview__Show as Show,
} from './preview.generated';

const dedupe = <T extends { id: number }>(items: readonly T[]): readonly T[] => {
  const map = new Map<number, T>(items.map((item) => [item.id, item]));
  return [...map.values()];
};

const empty = { aspectRatio: 16 / 9, payload: null };

const contentItemTransform =
  (contentItems: readonly ContentItem[]) => (contentItem: ApiContentItem) => {
    const showContentItem = contentItems.find(({ id }) => contentItem.id === id);
    assert(showContentItem != null, 'content item not found by id, bad!');

    const { contentOptions, file, templateVersionId } = showContentItem;

    return {
      ...contentItem,
      templateUri: `/template-versions/${templateVersionId}/index.html?url=${encodeURIComponent(
        file.uri,
      )}`,
      contentOptions: contentOptions.map((contentOption) => {
        if (
          contentOption.array &&
          ['IMAGE', 'MEDIA', 'PDF', 'POWERPOINT', 'VIDEO'].includes(contentOption.kind)
        ) {
          return contentOption;
        }
        const value = ['IMAGE', 'PDF', 'POWERPOINT', 'VIDEO'].includes(contentOption.kind)
          ? contentOption.mediaItem?.file?.uri ?? null
          : contentOption.value ?? null;
        return { ...contentOption, value };
      }),
    };
  };

const stripTypename = <T extends { __typename: string }>(object: T | null | undefined) => {
  if (object == null) return null;
  const { __typename, ...rest } = object;
  return rest;
};

export const toClientManifest = (
  apiManifest: ApiManifest,
  contentItems: readonly ContentItem[],
) => {
  const shows = apiManifest.shows.map(({ channelGuide, ...show }) => ({
    ...show,
    channelGuide:
      channelGuide == null
        ? undefined
        : {
            ...channelGuide,
            channels: channelGuide.channels.map((channel) => {
              const file = apiManifest.files.find((file) => file.id === channel.thumbnailFileId);
              assert(file !== undefined, 'Channel thumbnailFileId not in files');
              // TODO: fix for CMS
              const thumbnailUri = `/files/${file.id}/${file.filename}`;
              return { ...channel, thumbnailUri };
            }),
          },
  }));

  // TODO: __typename in duration breaks sync zones - use ISO duration strings instead
  const playlists = apiManifest.playlists.map((playlist) => ({
    ...playlist,
    playlistItems: playlist.playlistItems.map((playlistItem) => ({
      ...playlistItem,
      duration: stripTypename(playlistItem.duration),
    })),
  }));

  return {
    ...apiManifest,
    contentItems: apiManifest.contentItems.map(contentItemTransform(contentItems)),
    playlists,
    shows,
  };
};

export const playlistPreviewProps = (playlist: Playlist | undefined | null) => {
  if (playlist == null) return empty;

  const layout = {
    __typename: 'Layout' as const,
    id: 1,
    name: 'Preview Layout',
    width: 1920,
    height: 1080,
    zones: [
      {
        __typename: 'Zone' as const,
        id: 1,
        layoutId: 1,
        name: 'Main Zone',
        left: 0,
        top: 0,
        width: 1920,
        height: 1080,
      },
    ],
  };

  const view = {
    __typename: 'View' as const,
    id: 1,
    layoutId: layout.id,
    layout,
    name: 'Preview View',
    viewZones: [
      {
        __typename: 'ViewZone' as const,
        id: 1,
        name: 'Main ViewZone',
        reportPlayback: false,
        sync: false,
        viewZonePlaylists: [
          {
            __typename: 'ViewZonePlaylist' as const,
            id: 1,
            playlistId: playlist.id,
            playlist,
          },
        ],
        zoneId: 1,
      },
    ],
  };

  const show = {
    __typename: 'Show' as const,
    id: 1,
    name: `Preview: ${playlist.name}`,
    defaultViewId: view.id,
    serviceIds: [],
    views: [view],
    channelGuide: null,
    fanconnect: null,
    properties: [],
  };

  const mapContentItem = (contentItem: ContentItem) => ({
    ...contentItem,
    contentOptions: contentItem.contentOptions.map(({ value = null, ...contentOption }) => ({
      ...contentOption,
      value,
    })),
  });

  const contentItems = dedupe(
    playlist.playlistItems.map(({ contentItem }) => mapContentItem(contentItem)),
  );

  const apiManifest: ApiManifest = {
    __typename: 'Manifest',
    contentItems,
    device: {
      __typename: 'Device',
      api: false,
      closedCaptioning: false,
      defaultChannel: null,
      id: 0,
      name: '',
      network: {
        __typename: 'Network',
        encryptionKey: '',
        id: 0,
        snmpCommunity: null,
      },
      eventStream: false,
      kind: 'WEB',
      nightlyBehavior: 'NONE',
      preload: true,
      properties: [],
      remoteTargets: [],
      snmpService: false,
      timeZone: 'local',
    },
    files: [],
    layouts: [layout],
    overlays: [],
    playlists: [playlist],
    shows: [show],
  };

  const manifest = toClientManifest(apiManifest, contentItems);

  return {
    aspectRatio: layout.width / layout.height,
    payload: {
      manifest,
      viewId: view.id,
    },
  };
};

export const showPreviewProps = (
  show: Show | undefined | null,
  viewId: number | '',
  device?: Preview__Device,
): Pick<PreviewProps, 'aspectRatio' | 'payload'> => {
  if (show == null || viewId === '') return empty;

  const originalView = show.views.find((view) => view.id === viewId);

  if (originalView == null) {
    console.warn(`Couldn't find view with ID ${viewId}`);
    return empty;
  }

  const view = {
    ...originalView,
    viewZones: originalView.viewZones.map((x) => ({ ...x, reportPlayback: false, sync: false })),
  };

  const layout = view.layout;

  const playlists = dedupe(
    show.views.flatMap(({ viewZones }) =>
      viewZones.flatMap(({ viewZonePlaylists }) =>
        viewZonePlaylists.flatMap(({ playlist }) => playlist),
      ),
    ),
  );

  const mapContentItem = (contentItem: ContentItem) => ({
    ...contentItem,
    contentOptions: contentItem.contentOptions.map(({ value = null, ...contentOption }) => ({
      ...contentOption,
      value,
    })),
  });

  const contentItems = dedupe([
    ...playlists.flatMap(({ playlistItems }) =>
      playlistItems.map(({ contentItem }) => mapContentItem(contentItem)),
    ),
  ]);

  const channelGuide: ApiChannelGuide | null =
    show.channelGuide == null
      ? null
      : {
          __typename: 'ChannelGuide',
          id: show.channelGuide.id,
          channels: show.channelGuide.channels.map((channel) => ({
            ...channel,
            thumbnailFileId: channel.thumbnailFile.id,
            thumbnailUri: channel.thumbnailFile.uri,
          })),
          contentItemId: show.channelGuide.contentItemId,
          defaultChannel: show.channelGuide.defaultChannel,
          name: show.channelGuide.name,
        };

  // FIXME: toClientManifest requires this
  const files =
    show.channelGuide?.channels.map(
      ({ thumbnailFile }): ApiFile => ({
        __typename: 'File',
        bucket: '',
        filename: '',
        id: thumbnailFile.id,
        kind: 'FILE',
        md5Sum: '',
        objectKey: '',
        size: 0,
      }),
    ) ?? [];

  const apiManifest: ApiManifest = {
    __typename: 'Manifest',
    contentItems,
    device: {
      __typename: 'Device',
      api: false,
      closedCaptioning: false,
      defaultChannel: device?.defaultChannel,
      id: device?.id ?? 0,
      name: '',
      network: {
        __typename: 'Network',
        encryptionKey: '',
        id: 0,
        snmpCommunity: null,
      },
      eventStream: false,
      kind: 'WEB',
      nightlyBehavior: 'NONE',
      preload: device?.preload ?? true,
      properties: [],
      remoteTargets: [],
      snmpService: false,
      timeZone: device?.timeZone ?? 'local',
    },
    files,
    layouts: [layout],
    overlays: [],
    playlists,
    shows: [{ ...show, channelGuide, defaultViewId: view.id, serviceIds: [], views: [view] }],
  };

  const manifest = toClientManifest(apiManifest, contentItems);
  // HACK: toClientManifest munges the thumbnailUri
  Object.assign(manifest.shows[0], { channelGuide });

  return { aspectRatio: layout.width / layout.height, payload: { manifest, viewId } };
};
